import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import type { PassportDetail } from "../api/types";
import { combineEt, formatDisplay, formatHalfHour, HALF_HOURS, splitEt, todayISODate } from "../lib/dates";
import { AppleWalletMark } from "./AppleWalletMark";
import { AuthImage } from "./AuthImage";
import { WalletAddQr } from "./WalletAddQr";

export type ScheduleSave = {
  suite: string;
  schedDate: string;
  schedTime: string;
  facility: string;
  arriveTime: string;
};

export function scheduleSaveBody(fields: ScheduleSave): Record<string, string | null> {
  const scheduled = combineEt(fields.schedDate, fields.schedTime);
  const arriveDate = fields.schedDate.trim() || todayISODate();
  return {
    or_suite: fields.suite.trim() || null,
    facility: fields.facility.trim() || null,
    scheduled_or_at: scheduled,
    facility_at: combineEt(arriveDate, fields.arriveTime),
  };
}

function TimeSelect({
  id,
  name,
  defaultValue,
}: {
  id: string;
  name: string;
  defaultValue: string;
}) {
  return (
    <select id={id} name={name} defaultValue={defaultValue}>
      <option value="" />
      {HALF_HOURS.map((hm) => (
        <option key={hm} value={hm}>
          {formatHalfHour(hm)}
        </option>
      ))}
    </select>
  );
}


function clinicSigLabel(role: string) {
  if (role === "patient") return "Patient Signature";
  if (role === "surgeon") return "Surgeon Signature";
  if (role === "witness") return "Witness";
  if (role === "circulator") return "Circulator";
  if (role === "cancel") return "Cancel Surgical Passport";
  return role;
}

export async function confirmDeletePassport(code: string, id: number): Promise<boolean> {
  if (!window.confirm(`Delete ${code}?`)) return false;
  await api(`/api/portal/passports/${id}`, { method: "DELETE" });
  return true;
}

export function ScheduleForm({
  idPrefix,
  formKey,
  defaultSuite,
  defaultSched,
  defaultFacility,
  defaultArrive,
  onSave,
}: {
  idPrefix: string;
  formKey: string | number;
  defaultSuite: string;
  defaultSched: string;
  defaultFacility: string;
  defaultArrive: string;
  onSave: (fields: ScheduleSave) => void;
}) {
  const sched = splitEt(defaultSched);
  const arrive = splitEt(defaultArrive);
  const dateId = `${idPrefix}-sched-date`;
  const timeId = `${idPrefix}-sched-time`;
  const arriveId = `${idPrefix}-arrive`;
  const facilityId = `${idPrefix}-facility`;
  const suiteId = `${idPrefix}-suite`;
  const [facilities, setFacilities] = useState<string[]>(() => (defaultFacility ? [defaultFacility] : []));

  useEffect(() => {
    let alive = true;
    api<string[]>("/api/portal/or-facilities")
      .then((names) => {
        if (!alive) return;
        const next = [...names];
        if (defaultFacility && !next.includes(defaultFacility)) next.unshift(defaultFacility);
        setFacilities(next);
      })
      .catch(() => {
        if (alive && defaultFacility) setFacilities([defaultFacility]);
      });
    return () => {
      alive = false;
    };
  }, [defaultFacility]);

  const facilityOptions = useMemo(() => facilities, [facilities]);

  return (
    <form
      key={formKey}
      onSubmit={(e) => {
        e.preventDefault();
        const fd = new FormData(e.currentTarget);
        onSave({
          suite: String(fd.get("suite") || ""),
          schedDate: String(fd.get("sched_date") || ""),
          schedTime: String(fd.get("sched_time") || ""),
          facility: String(fd.get("facility") || ""),
          arriveTime: String(fd.get("arrive") || ""),
        });
      }}
    >
      <label htmlFor={dateId}>Scheduled OR time</label>
      <div className="sched-pair">
        <input id={dateId} name="sched_date" type="date" defaultValue={sched.date} />
        <TimeSelect id={timeId} name="sched_time" defaultValue={sched.time} />
      </div>
      <label htmlFor={arriveId}>Time to arrive</label>
      <TimeSelect id={arriveId} name="arrive" defaultValue={arrive.time} />
      <label htmlFor={facilityId}>Facility</label>
      <select key={facilityOptions.join("|")} id={facilityId} name="facility" defaultValue={defaultFacility}>
        <option value="" />
        {facilityOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
      <label htmlFor={suiteId}>OR suite</label>
      <input id={suiteId} name="suite" defaultValue={defaultSuite} placeholder="OR-3" />
      <div className="suite-row">
        <button type="submit">Save</button>
      </div>
    </form>
  );
}

type Props = {
  detail: PassportDetail;
  printBusy: string;
  archiveBusy: boolean;
  defaultSuite: string;
  defaultSched: string;
  defaultFacility: string;
  defaultArrive: string;
  onClose: () => void;
  onSave: (id: number, fields: ScheduleSave) => Promise<void>;
  onPrint: (kind: "consent" | "report" | "record") => Promise<void>;
  onArchive: () => Promise<void>;
  onDelete?: () => Promise<void>;
  deleteBusy?: boolean;
};

export function PassportDrawer({
  detail,
  printBusy,
  archiveBusy,
  defaultSuite,
  defaultSched,
  defaultFacility,
  defaultArrive,
  onClose,
  onSave,
  onPrint,
  onArchive,
  onDelete,
  deleteBusy,
}: Props) {
  const canPrint = Boolean(detail.locked_at) || detail.status !== "draft";
  const canArchive = detail.status === "incision_verified" && !detail.case_completed_at;

  return (
    <div className="drawer-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="drawer" role="dialog" aria-label="Passport detail">
        <button type="button" className="close" onClick={onClose}>
          Close
        </button>
        <h2>
          {detail.passport_code}
          <AppleWalletMark show={detail.in_apple_wallet} />
        </h2>
        <div className="muted">{detail.patient_name}</div>
        <Link className="drawer-open-record" to={`/passports/${detail.id}`} onClick={onClose}>
          Passport
        </Link>
        <div className="section">
          <h4>Case</h4>
          <div>
            <strong>{detail.procedure}</strong>
          </div>
          <div className="lat">{detail.laterality}</div>
          <div className="muted">Surgeon {detail.surgeon_name}</div>
          <div className="muted">Encounter {formatDisplay(detail.encounter_at)}</div>
        </div>
        <div className="section">
          <h4>Print</h4>
          <div className="print-row">
            <button type="button" disabled={!canPrint || printBusy === "consent"} onClick={() => onPrint("consent")}>
              {printBusy === "consent" ? "Preparing…" : "Print consent"}
            </button>
            <button type="button" disabled={!canPrint || printBusy === "report"} onClick={() => onPrint("report")}>
              {printBusy === "report" ? "Preparing…" : "Print report"}
            </button>
            <button type="button" disabled={!canPrint || printBusy === "record"} onClick={() => onPrint("record")}>
              {printBusy === "record" ? "Preparing…" : "Print record"}
            </button>
            {onDelete && (
              <button type="button" className="danger" disabled={deleteBusy} onClick={() => void onDelete()}>
                {deleteBusy ? "Working…" : "Delete"}
              </button>
            )}
          </div>
          {!canPrint && <div className="empty">Lock passport before printing.</div>}
        </div>
        <WalletAddQr passportId={detail.id} show={detail.status !== "draft"} />
        {canArchive && (
          <div className="section">
            <div className="print-row">
              <button type="button" disabled={archiveBusy} onClick={() => void onArchive()}>
                {archiveBusy ? "Working…" : "Archive"}
              </button>
            </div>
          </div>
        )}
        <div className="section">
          <h4>Schedule / suite</h4>
          <ScheduleForm
            idPrefix="drawer"
            formKey={detail.id}
            defaultSuite={defaultSuite}
            defaultSched={defaultSched}
            defaultFacility={defaultFacility}
            defaultArrive={defaultArrive}
            onSave={(fields) => void onSave(detail.id, fields)}
          />
        </div>
        <div className="section">
          <h4>Signatures</h4>
          {(detail.signatures || []).length === 0 && <div className="empty">No signatures yet.</div>}
          {(detail.signatures || []).map((s, i) => (
            <div className="sig" key={`${s.role}-${i}`}>
              <div>
                <strong>{clinicSigLabel(s.role)}</strong> · {s.printed_name}
              </div>
              <div className="muted">{formatDisplay(s.signed_at)}</div>
              {s.image_path ? (
                <AuthImage className="sig-pad" path={`/api/passports/${detail.id}/signatures/${s.role}`} alt={clinicSigLabel(s.role)} />
              ) : null}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
